import {Component, OnDestroy, OnInit} from '@angular/core';
import {DecimalPipe, NgForOf, NgIf} from '@angular/common';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {Subscription} from 'rxjs';

import {
  CriterionDef,
  CriterionGroup,
  EvaluationSeason,
  EvaluationState,
  GROUP_LABELS,
  GROUP_ORDER,
  STATE_LABELS,
} from '../../model/evaluation/evaluation';
import {EvaluationsService} from '../../services/evaluations.service';

interface CriterionRow {
  def: CriterionDef;
  groupStart: string | null;
  /** What this criterion does to the payment, in the season's own numbers. */
  effect: string;
}

interface StateRow {
  state: EvaluationState;
  visibleToProducer: boolean;
  whoActs: string;
  what: string;
}

/**
 * How the evaluation works, written for the people who use it.
 *
 * It lives in the app rather than in a document nobody can find, and it takes
 * its numbers from the published season — the rates, the cuts and the list of
 * criteria all come from `evaluations/{season}`, so a new contract restates
 * the page instead of quietly contradicting it. Prose that repeats a rate is
 * prose that will be wrong next season.
 *
 * It is deliberately about **this app**: what the states mean, who may do
 * what, and how the amount is arrived at. How a broadcast is analysed before
 * it arrives is not described here and should not be added — that is the
 * association's own work, and a page every production company can read is
 * the wrong place for it.
 */
@Component({
  selector: 'app-evaluation-guide',
  templateUrl: './guide.component.html',
  styleUrls: ['./guide.component.scss'],
  standalone: true,
  imports: [MatCardModule, MatIconModule, DecimalPipe, NgForOf, NgIf],
})
export class EvaluationGuideComponent implements OnInit, OnDestroy {

  season: EvaluationSeason;
  criteria: CriterionRow[] = [];
  states: StateRow[] = [];
  /** Everything a faultless broadcast pays, so the cuts have a scale. */
  maximum = 0;

  private subscriptions = new Subscription();

  constructor(private evaluations: EvaluationsService) {
  }

  ngOnInit(): void {
    this.subscriptions.add(this.evaluations.season().subscribe(season => {
      this.season = season;
      if (season) {
        this.build();
      }
    }));
    this.states = [
      {
        state: 'toDecide', visibleToProducer: false, whoActs: 'The association',
        what: 'Anything the analysis would not decide is settled here. The '
          + 'broadcast is not visible to the production company yet — the '
          + 'numbers are still internal.',
      },
      {
        state: 'toApprove', visibleToProducer: true, whoActs: 'The production company',
        what: 'Released. Check the criteria and the amount, say whether the '
          + 'broadcast had an operated camera, then approve it or send it back.',
      },
      {
        state: 'returned', visibleToProducer: true, whoActs: 'The association',
        what: 'Sent back with a note. The association answers it and releases '
          + 'the broadcast again; this can go round as often as it needs to.',
      },
      {
        state: 'approved', visibleToProducer: true, whoActs: 'Nobody',
        what: 'The amount is agreed and frozen, together with the rates it was '
          + 'worked out from. A later change to the rate card does not restate it.',
      },
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private build(): void {
    const ordered = [...(this.season.criteria ?? [])]
      .sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
    let group: CriterionGroup | null = null;
    this.criteria = ordered.map(def => {
      const groupStart = def.group === group ? null : GROUP_LABELS[def.group];
      group = def.group;
      return {def, groupStart, effect: this.effectOf(def)};
    });

    const rates = this.season.rates;
    this.maximum = rates.production + rates.commentary + rates.replay + rates.operatedCamera;
  }

  /**
   * What one criterion is worth, said in money rather than in jargon. The
   * roles are the app's words (`gate`, `penalty`, `paid`); nobody signing an
   * evaluation should have to learn them.
   */
  private effectOf(def: CriterionDef): string {
    const rates = this.season.rates;
    const cuts = this.season.penalties;
    const percent = (share: number) => `${Math.round(share * 100)} %`;

    switch (def.role) {
      case 'gate':
        return 'Absolute condition — if it is not met, the broadcast pays nothing at all.';
      case 'info':
        return 'Measured and shown, but not paid for: what it consists of is '
          + 'already covered by the conditions and the cuts above.';
      case 'declared':
        return `Declared by the production company: none, ${this.money(rates.operatedCamera / 2)} `
          + `for an operated camera that was not on live play, or `
          + `${this.money(rates.operatedCamera)} for one that was.`;
      case 'paid':
        if (def.id === 'liveCommentary') {
          return `${this.money(rates.commentary)} — except on a broadcast that also `
            + 'went out on ČT Sport+, where the commentary is paid by the '
            + 'association and Czech Television instead.';
        }
        if (def.id === 'replay') {
          return `${this.money(rates.replay)}.`;
        }
        return `${this.money(rates.extraVideo)} per video, on top of the mandatory set.`;
      case 'penalty':
        if (def.group === 'sponsor') {
          return `Each message that did not run takes ${percent(cuts.sponsorMessage)} off `
            + 'the production fee.';
        }
        if (def.id === 'opener') {
          return `Not paid for on its own; if it did not run it takes `
            + `${percent(cuts.opener)} off the production fee.`;
        }
        if (def.id === 'technical') {
          return `Up to ${percent(cuts.technical ?? 0)} off the production fee, scaled by how `
            + 'far short the broadcast fell. The only criterion the association '
            + 'sets as a range rather than yes or no.';
        }
        return `One of the two presentations missing takes ${percent(cuts.lineupsPartial)}; `
          + `both missing takes ${percent(cuts.lineupsMissing)}.`;
      default:
        return '';
    }
  }

  money(amount: number): string {
    return `${amount.toLocaleString('cs-CZ')} ${this.season?.currency ?? ''}`.trim();
  }

  label(state: EvaluationState): string {
    return STATE_LABELS[state];
  }
}
